<template>
  <div
    v-bind="$attrs"
    class="gmf-fullbody-avatar gmf-fullbody-avatar-component"
    :class="classes"
    v-if="! reloading"
    @click="onClick"
  >
    <div :style="computedSkinStyle" class="gmf-fullbody-profile-skin"></div>

    <Image
      :src="avatarUrl + overrideQuery"
      :highSrc="computedHighSrc"
      :lazy="lazy && computedHighSrc"
      classes="gmf-fullbody-avatar-content"
      alt="Avatar"
    />

    <div v-if="customizable" class="gmf-fullbody-avatar-customizable">
      <Icon icon="change_avatar" />
    </div>

    <slot />
  </div>
</template>

<style>
.gmf-context .gmf-fullbody-avatar {
  aspect-ratio: 1;
  width: 100%;
  max-width: 450px;
  background: var(--gmf-fullbody-avatar-bg);
}
.gmf-fullbody-avatar-component {
  line-height: 0 !important;
  position: relative;
}

.gmf-fullbody-avatar-component.gmf-fullbody-avatar-popout:has(.gmf-fullbody-avatar-loading) {
  background: none;
  background-color: inherit;
}

.gmf-fullbody-avatar-content {
  aspect-ratio: 1;
  width: 100%;
  height: 100%;
  position: absolute;
  inset: 0;
  object-fit: contain;
}
.gmf-fullbody-avatar-content img {
  width: 100%;
  height: 100%;
}

.gmf-fullbody-avatar.gmf-skin-background {
  background: white;
}
.gmf-fullbody-avatar.gmf-skin-background.gmf-dark {
  background: black;
}

.gmf-status-indicator.gmf-offline::before {
  content: v-bind('lastOnline');
}
.gmf-fullbody-avatar-customizable {
  top: 100%;
  left: 13%;
  position: absolute;
  transform: translate(-50%, -50%);
}

.gmf-fullbody-avatar-customizable .gmf-icon {
  height: 32px;
  min-width: 32px;
  padding: 3px;
  background: var(--gmf-sidemenu-background-color);
  border-radius: 100%;
  --gmf-icon-color: var(--gmf-sidemenu-text-color);
}


.gmf-fullbody-avatar-popout .gmf-fullbody-avatar-content {
  left: -15%;
  right: -15%;
  top: -25%;
  bottom: -5%;
  width: 130%;
  height: 130%;
  max-width: unset;
  max-height: unset;
  background: none !important;
  border-radius: 100%;
}

.gmf-fullbody-avatar-popout.gmf-fullbody-avatar-3d-fullbody .gmf-fullbody-avatar-content {
  left: -50%;
  right: -50%;
  top: -60%;
  bottom: -40%;

  height: 200%;
  width: auto;
  margin: auto;
  border-radius: unset;
}

.gmf-context .gmf-fullbody-avatar-initials-container {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  container-type: size;
}
.gmf-fullbody-avatar-initials-container .gmf-fullbody-avatar-content {
  position: absolute; 
  inset: 0;
}
.gmf-context .gmf-fullbody-avatar-initials-container span {
  letter-spacing: 1px;
  padding-left: 1px;
  text-transform: uppercase;
  font-family: var(--gmf-header-font);
  color: var(--gmf-col-darkgray);
  font-size: 18px;
  font-size: 40cqw;
  font-weight: bold;
}

.gmf-fullbody-avatar-3d.gmf-fullbody-avatar-placeholder .gmf-fullbody-avatar-content {
  filter: brightness(0) invert(0.8);
}

.gmf-fullbody-profile-skin {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
</style>

<script>
import state from '@/state';
import Image from '@/components/Image';
import eventbus from '@/utils/eventbus';
import openPopupProfile from '@/components/PopupProfileCard/openPopupProfileCard';
import Icon from '@/components/Icon';
import isSafari from '@/utils/isSafari';

export default {
  components: { Image, Icon },
  emits: ['customize'],
  props: {
    customer: {
      type: Object,
      require: true,
    },
    disablePopup: {
      type: Boolean,
      default: false,
    },
    disableObjectHack: {
      type: Boolean,
      default: false,
    },
    disableOnlineIndicator: {
      type: Boolean,
      default: false,
    },
    customizable: {
      type: Boolean,
      default: false,
    },
    defaultPopupTab: {
      type: String,
      default: 'achievements',
    },
    disablePopout: {
      type: Boolean,
      default: false,
    },
    animated: {
      type: Boolean,
      default: false,
    },
    lazy: {
      type: Boolean,
      default: false,
    },
    paused: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      state,
      reloading: false,
      imgLoading: null,
      show3DAvatar: true,
      tempDisableAnim: false,
    };
  },
  mounted () {
    eventbus.on('avatar:update', this.updateAvatarCode);
  },
  unmounted () {
    eventbus.off('avatar:update', this.updateAvatarCode);
  },
  computed: {
    computedHighSrc () {
      return (this.useAnimated3DAvatars && this.animatedAvatarUrl && !this.paused && (this.animatedAvatarUrl + this.overrideQuery)) || null;
    },
    overrideQuery () {
      const ts = state.avatarTimestamps[this.customer.id];
      if (! ts) {
        return '';
      }
      return '?ts=' + ts;
    },
    classes () {
      return {
        'gmf-clickable-wrapper': this.isClickable,
        'gmf-skin-background': this.profileSkin, 
        'gmf-dark': this.profileSkin?.dark
      };
    },
    useAnimated3DAvatars () {
      const a = this.customer;

      if (a) {
        if (a.isGuest) return false;
        if (a.merchantId && a.merchantId !== state.merchantId) return false;
      }
      return state.merchantConfig.avatars.animation?.enabled && this.animated;
    },
    avatarUrl () {
      if (! this.customer) {
        return this.placeholderUrl;
      }
      return `${state.cdnBase}/v2/avatar/fullbody/${this.merchantId}/${this.customer?.id}`;
    },
    animatedAvatarUrl () {
      return `${state.cdnBase}/v2/animated-avatar/fullbody/${this.merchantId}/${this.customer.id}`;
    },
    isMyProfile() {
      return state.profile && this.customer?.id === state.profile.id;
    },
    profileSkin() {
      if (! this.enabledSkins) {
        return null;
      }

      // My profile skins
      if (this.isMyProfile) {
        return state.profile.profileSkin;
      }

      // Other user profile skins
      return this.customer?.profileSkin;
    },
    enabledSkins() {
      return state.merchantConfig.widgets?.skins?.enabled;
    },
    computedSkinStyle() {
      if (! this.enabledSkins) {
        return;
      }

      return {
        backgroundImage: `url(${this.profileSkin?.imageUrl || this.profileSkin?.image_url})`,
        opacity: this.profileSkin?.opacity,
      };
    },
    isClickable () {
      return this.customizable || this.popupProfileCardEnabled;
    },
    popupProfileCardEnabled () {
      return this.publicProfile && !this.disablePopup && state.merchantConfig?.popupProfileCard?.enabled;
    },
    merchantId () {
      return this.customer?.merchantId || state.merchantId;
    },
    publicProfile () {
      const a = this.customer;

      if (! a) return false;
      if (a.merchantId && a.merchantId !== state.merchantId) return false;
      return ! a.isGuest;
    },
  },
  methods: {
    onClick (ev) {
      if (! this.customizable) {
        return;
      }
      this.$emit('customize', ev);
      ev.stopPropagation();
      ev.preventDefault();
    },
    openProfileCard (ev) {
      ev.stopPropagation();
      ev.preventDefault();
      openPopupProfile(this.customer.id, this.defaultPopupTab);
    },
    updateAvatarCode () {
      if (this.profile?.id === this.customer.id) {
        this.swapImgInDOM();
      }
    },
    swapImgInDOM () {
      this.reloading = true;
      if (isSafari) {
        setTimeout(() => {
          this.reloading = false;
        }, 500);
      } else {
        this.$nextTick(() => {
          this.reloading = false;
        });
      }
    },
  },
  watch: {
    reloading() {
      this.$emit("update:loading", this.reloading);
    }
  }
};
</script>
